import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../App';
import QrScannerModal from '../components/QrScannerModal';
import MoClientMonitor from '../components/MoClientMonitor';
import { useMoControllerStore } from '../stores/moControllerStore';
import { formatDate, formatDateTime } from '../utils/datetime';

const formatTimeAgo = (date: Date): string => {
 const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
 if (seconds < 60) return `${seconds}초 전`;
 const minutes = Math.floor(seconds / 60);
 if (minutes < 60) return `${minutes}분 전`;
 const hours = Math.floor(minutes / 60);
 if (hours < 24) return `${hours}시간 전`;
 const days = Math.floor(hours / 24);
 return `${days}일 전`;
};

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// 화면별 일일 상태 리포트 전송 시각 편집기. 매장 영업(전원 ON) 시간대에 맞춰 지정.
const ReportTimeEditor: React.FC<{ clientHash: string }> = ({ clientHash }) => {
  const [time, setTime] = useState<string>('18:00');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    fetch(`/moclient/${clientHash}/report-time`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(b => { if (alive && b?.success) setTime(b.data?.report_time || '18:00'); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [clientHash]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`/moclient/${clientHash}/report-time`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_time: time }),
      });
      const b = await r.json();
      setMsg(r.ok && b?.success ? '저장됨' : (b?.error || '저장 실패'));
    } catch {
      setMsg('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b pb-4">
      <h3 className="font-semibold text-lg mb-1">일일 상태 리포트 시각</h3>
      <p className="text-xs text-gray-500 mb-2">
        이 화면이 매일 1회 상태(메모리·디스크·재생 파일 수·온도/쓰로틀)를 서버로 보내는 시각입니다.
        매장 영업 중(전원 ON) 시간으로 맞추세요. 기본 18:00.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="time"
          value={time}
          onChange={(e) => { setTime(e.target.value); setMsg(null); }}
          disabled={!loaded || saving}
          className="px-2 py-1 border border-gray-300"
        />
        <button
          type="button"
          onClick={save}
          disabled={!loaded || saving || !HHMM_RE.test(time)}
          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:bg-gray-300"
        >
          {saving ? '저장 중…' : '저장'}
        </button>
        {msg && <span className="text-xs text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

// 화면 물리 설치 방향(회전) 편집기. 세로 설치 화면에 세로 콘텐츠를 정립 재생하기 위해
// 회전각을 서버에 저장한다(방향의 단일 권위). 회전본은 인코딩 시점에 구워진다.
// rotation = 패널이 물리적으로 시계방향(CW)으로 돈 각도.
const ROTATION_OPTIONS: { value: 0 | 90 | 270; label: string }[] = [
  { value: 0, label: '없음 (가로 설치)' },
  { value: 90, label: '오른쪽으로 90° (세로 설치)' },
  { value: 270, label: '왼쪽으로 90° (세로 설치)' },
];

const RotationEditor: React.FC<{
  clientHash: string;
  initialRotation: 0 | 90 | 270;
  thumbnailUrl?: string | null;
  progress?: { ready: number; total: number } | null;
  onChanged?: () => void;
}> = ({ clientHash, initialRotation, thumbnailUrl, progress, onChanged }) => {
  const [rotation, setRotation] = useState<0 | 90 | 270>(initialRotation);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { setRotation(initialRotation); setMsg(null); }, [initialRotation, clientHash]);

  // 미리보기는 패널 물리 회전각을 그대로 CSS 회전 — 사용자가 "실제로 이 방향으로 돌렸나"를
  // 눈으로 대조해 방향 오입력(90↔270, 180° 뒤집힘)을 인코딩 전 차단. 90°=오른쪽(CW),
  // 270°=왼쪽(CCW → rotate(-90)).
  const previewDeg = rotation === 270 ? -90 : rotation;

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`/moclient/${clientHash}/rotation`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rotation }),
      });
      const b = await r.json();
      if (r.ok && b?.success) { setMsg('저장됨 — 회전본 인코딩이 준비됩니다.'); onChanged?.(); }
      else setMsg(b?.error || '저장 실패');
    } catch { setMsg('저장 실패'); }
    finally { setSaving(false); }
  };

  const dirty = rotation !== initialRotation;

  return (
    <div className="border-b pb-4">
      <h3 className="font-semibold text-lg mb-1">화면 설치 방향</h3>
      <p className="text-xs text-gray-500 mb-2">
        화면을 세로로 설치했다면 물리적으로 돌린 방향을 선택하세요. 세로 콘텐츠가 정립으로
        재생되도록 회전본이 자동 인코딩됩니다. 오른쪽 미리보기가 실제 설치한 화면 방향과
        같은지 확인하세요(반대면 다른 쪽 선택).
      </p>
      <div className="flex items-start gap-4">
        <div className="flex flex-col gap-2">
          <select
            value={rotation}
            onChange={(e) => { setRotation(Number(e.target.value) as 0 | 90 | 270); setMsg(null); }}
            disabled={saving}
            className="px-2 py-1 border border-gray-300"
          >
            {ROTATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm disabled:bg-gray-300"
            >
              {saving ? '저장 중…' : '저장'}
            </button>
            {msg && <span className="text-xs text-gray-600">{msg}</span>}
          </div>
          {rotation !== 0 && progress && (
            <p className="text-xs text-gray-600">
              회전본 준비: <span className="font-medium">{progress.ready}/{progress.total}</span>
              {progress.total > 0 && progress.ready < progress.total && ' (인코딩 중 — 준비 전엔 임시로 가로 재생될 수 있음)'}
            </p>
          )}
        </div>
        {/* 미리보기 — 소스 썸네일(없으면 방향 표식)을 pre-rotation 만큼 CSS 회전 */}
        <div className="shrink-0 w-28 h-28 border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          <div style={{ transform: `rotate(${previewDeg}deg)`, transition: 'transform 0.2s' }}
               className="w-24 flex items-center justify-center">
            {thumbnailUrl
              ? <img src={thumbnailUrl} alt="방향 미리보기" className="w-24 object-contain" />
              : <div className="w-24 h-14 bg-gray-300 text-gray-700 text-[10px] flex items-start justify-center pt-0.5">▲ 위쪽</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

interface MoFamily {
 moFamilyHash: string;
 name: string;
 description?: string;
 isOwner: boolean;
 role?: string;
 collaboratingCount: number;
 totalSlots?: number;
 ownedSlots?: number;
 maxMembers?: number;
 createdAt?: string;
 ownerMoPeerHash?: string;
 ownerName?: string;
}

interface MoClient {
 clientHash: string;
 name: string;
 description?: string;
 moFamilyHash: string;
 moFamilyName: string;
 ownerMoPeerHash: string;
 ownerEmail: string;
 ownerName?: string;
 isActive: boolean;
 createdAt: string;
 updatedAt: string;
 heartbeat?: number;
 lastHeartbeat?: string;
 status?: Record<string, unknown>;
 latitude?: number;
 longitude?: number;
 address?: string;
 nowPlaying?: string;
 nowPlayingThumbnail?: string;
 nowPlayingFilename?: string;
 comment?: string;
 componentVersions?: Record<string, string>;
 platform?: string;
 installationPhotos?: { filename: string; url: string }[];
 rotation?: 0 | 90 | 270;
 rotationVariantProgress?: { ready: number; total: number } | null;
}

interface HierarchyTicket {
 moFamilyHash: string;
 familyName: string;
 ticketHash: string; // 티켓이 곧 인증 키
 hierarchyLevel: number;
}

// Google Maps type declarations
declare global {
 interface Window {
  google: any;
 }
}

interface LocationData {
 latitude: number | null;
 longitude: number | null;
 address: string;
}

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 }; // Seoul

const reverseGeocode = (lat: number, lng: number): Promise<string> => {
 return new Promise((resolve) => {
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
   if (status === 'OK' && results[0]) {
    resolve(results[0].formatted_address);
   } else {
    resolve(`${lat.toFixed(8)}, ${lng.toFixed(8)}`);
   }
  });
 });
};

const LocationMapEdit: React.FC<{
 latitude: number | null;
 longitude: number | null;
 address: string;
 onChange: (loc: LocationData) => void;
}> = ({ latitude, longitude, address, onChange }) => {
 const mapRef = useRef<HTMLDivElement>(null);
 const mapInstanceRef = useRef<any>(null);
 const markerRef = useRef<any>(null);
 const searchInputRef = useRef<HTMLInputElement>(null);
 const [mapsReady, setMapsReady] = useState(false);

 useEffect(() => {
  const check = () => {
   if (window.google?.maps) {
    setMapsReady(true);
   } else {
    setTimeout(check, 200);
   }
  };
  check();
 }, []);

 useEffect(() => {
  if (!mapsReady || !mapRef.current) return;

  const center = latitude != null && longitude != null
   ? { lat: Number(latitude), lng: Number(longitude) }
   : DEFAULT_CENTER;

  const map = new window.google.maps.Map(mapRef.current, {
   center,
   zoom: latitude != null ? 16 : 6,
   mapTypeControl: false,
   streetViewControl: false,
   fullscreenControl: false,
  });
  mapInstanceRef.current = map;

  const marker = new window.google.maps.Marker({
   position: center,
   map,
   draggable: true,
   visible: latitude != null,
  });
  markerRef.current = marker;

  marker.addListener('dragend', async () => {
   const pos = marker.getPosition();
   const lat = pos.lat();
   const lng = pos.lng();
   const addr = await reverseGeocode(lat, lng);
   onChange({ latitude: lat, longitude: lng, address: addr });
  });

  map.addListener('click', async (e: any) => {
   const lat = e.latLng.lat();
   const lng = e.latLng.lng();
   marker.setPosition(e.latLng);
   marker.setVisible(true);
   const addr = await reverseGeocode(lat, lng);
   onChange({ latitude: lat, longitude: lng, address: addr });
  });

  // SearchBox
  if (searchInputRef.current) {
   const searchBox = new window.google.maps.places.SearchBox(searchInputRef.current);
   searchBox.addListener('places_changed', async () => {
    const places = searchBox.getPlaces();
    if (!places || places.length === 0) return;
    const place = places[0];
    if (!place.geometry?.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    map.setCenter(place.geometry.location);
    map.setZoom(16);
    marker.setPosition(place.geometry.location);
    marker.setVisible(true);
    const addr = place.formatted_address || place.name || await reverseGeocode(lat, lng);
    onChange({ latitude: lat, longitude: lng, address: addr });
   });
  }
 }, [mapsReady]);

 const handleGps = useCallback(() => {
  if (!navigator.geolocation) {
   alert('이 브라우저에서는 GPS를 사용할 수 없습니다.');
   return;
  }
  navigator.geolocation.getCurrentPosition(
   async (pos) => {
    const lat = pos.coords.latitude;
    const lng = pos.coords.longitude;
    const latLng = new window.google.maps.LatLng(lat, lng);
    if (mapInstanceRef.current) {
     mapInstanceRef.current.setCenter(latLng);
     mapInstanceRef.current.setZoom(16);
    }
    if (markerRef.current) {
     markerRef.current.setPosition(latLng);
     markerRef.current.setVisible(true);
    }
    const addr = await reverseGeocode(lat, lng);
    onChange({ latitude: lat, longitude: lng, address: addr });
   },
   (err) => {
    alert(`위치를 가져올 수 없습니다: ${err.message}`);
   },
   { enableHighAccuracy: true, timeout: 10000 }
  );
 }, [onChange]);

 if (!mapsReady) {
  return <div className="text-center py-8 text-gray-500">Google Maps 로딩 중...</div>;
 }

 return (
  <div className="space-y-3">
   <div className="flex gap-2">
    <input
     ref={searchInputRef}
     type="text"
     placeholder="장소 검색..."
     className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    <button
     type="button"
     onClick={handleGps}
     className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm whitespace-nowrap"
    >
     현위치
    </button>
   </div>
   <div ref={mapRef} style={{ width: '100%', height: '300px' }} />
   <div className="text-sm text-gray-600">
    <p><span className="font-medium">주소:</span> {address || '지도를 클릭하여 위치를 선택하세요'}</p>
    {latitude != null && longitude != null && (
     <p><span className="font-medium">좌표:</span> {Number(latitude).toFixed(8)}, {Number(longitude).toFixed(8)}</p>
    )}
   </div>
  </div>
 );
};

const LocationMapView: React.FC<{
 latitude: number | null;
 longitude: number | null;
 address: string | null;
}> = ({ latitude, longitude, address }) => {
 if (latitude == null || longitude == null) {
  return (
   <div className="space-y-2">
    <p className="text-gray-500">등록된 위치 없음</p>
    {address && (
     <div>
      <label className="text-sm text-gray-600">주소</label>
      <p className="font-medium">{address}</p>
     </div>
    )}
   </div>
  );
 }

 const lat = Number(latitude);
 const lng = Number(longitude);
 const src = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=600x200&markers=color:red|${lat},${lng}&key=AIzaSyBfGlhqrIrtTHnEb5dCPiYlshxtW_AonOM`;

 return (
  <div className="space-y-3">
   <img src={src} alt="위치 지도" style={{ width: '100%', height: 'auto' }} />
   <div className="text-sm text-gray-600">
    {address && <p><span className="font-medium">주소:</span> {address}</p>}
    <p><span className="font-medium">좌표:</span> {lat.toFixed(8)}, {lng.toFixed(8)}</p>
   </div>
  </div>
 );
};

const MoClientPage: React.FC = () => {
 const { user } = useAppStore();
 const [userOwnedFamilies, setUserOwnedFamilies] = useState<MoFamily[]>([]);
 const [showMoClientModal, setShowMoClientModal] = useState(false);
 const [moClientHash, setMoClientHash] = useState('');
 const [isRegisteringClient, setIsRegisteringClient] = useState(false);
 const [generatedMoClientHash, setGeneratedMoClientHash] = useState('');
 const [selectedMoFamilyForClient, setSelectedMoFamilyForClient] = useState('');
 const [registeredClients, setRegisteredClients] = useState<MoClient[]>([]);
 const [loadingClients, setLoadingClients] = useState(false);
 const [showDetailModal, setShowDetailModal] = useState(false);
 const [selectedClient, setSelectedClient] = useState<MoClient | null>(null);
 const [loadingDetail, setLoadingDetail] = useState(false);
 const [refreshingStatus, setRefreshingStatus] = useState(false);
 const [showRemote, setShowRemote] = useState(false);
 const volumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 const [localVolume, setLocalVolume] = useState<number | null>(null);
 const [editMode, setEditMode] = useState(false);
 const [editForm, setEditForm] = useState({ address: '', comment: '', latitude: null as number | null, longitude: null as number | null });
 const [photoUploading, setPhotoUploading] = useState(false);
 const [photoLightbox, setPhotoLightbox] = useState<string | null>(null);
 const photoInputRef = useRef<HTMLInputElement>(null);
 const [showTicketPoolModal, setShowTicketPoolModal] = useState(false);
 const [hierarchyTickets, setHierarchyTickets] = useState<HierarchyTicket[]>([]);
 const [loadingTickets, setLoadingTickets] = useState(false);
 const [ticketPoolClient, setTicketPoolClient] = useState<MoClient | null>(null);
 const [showQrScanner, setShowQrScanner] = useState(false);
 const [scannedDeviceName, setScannedDeviceName] = useState('');
 // Wi-Fi USB 생성 모달 — WPS 안 되는 환경에서 USB로 wifi 설정 주입
 const [showWifiUsbModal, setShowWifiUsbModal] = useState(false);
 const [wifiUsbSsid, setWifiUsbSsid] = useState('');
 const [wifiUsbPsk, setWifiUsbPsk] = useState('');
 const [wifiUsbSecurity, setWifiUsbSecurity] = useState<'WPA2' | 'WPA3' | 'OPEN'>('WPA2');
 const [wifiUsbDownloading, setWifiUsbDownloading] = useState(false);
 // Advanced — 모니터 진단 (Phase 2: 자기모순 자동 진단)
 type EdidIssue = {
   kind: 'range_h_max' | 'range_dotclock_max' | 'vsdb_max_tmds';
   msg: string;
   expected_min: number;
   actual: number;
 };
 type MonitorInfo = {
   connector: string;
   status: string;
   pnp_id: string | null;
   product_code: number | null;
   monitor_name: string | null;
   preferred_mode: string | null;
   available_modes: string[] | null;
   reported_at: string;
   diagnostics: {
     rangeLimits: { hMaxKhz: number; maxDotclockMhz: number } | null;
     vsdbMaxTmdsMhz: number | null;
     maxDtdHRateKhz: number;
     maxDtdPclkMhz: number;
     issues: EdidIssue[];
   } | null;
   auto_fix_active: boolean;
 };
 const [monitors, setMonitors] = useState<MonitorInfo[]>([]);
 const [showAdvanced, setShowAdvanced] = useState(false);
 const [expandedConn, setExpandedConn] = useState<string | null>(null);

 // Remote control
 const {
   playerStatus, playerConnected, connectionState, recentCommands, overlayToggles,
   detailedStatus, managerStatus, componentVersions: wsComponentVersions,
   selectClient: rcSelectClient, sendCommand: rcSendCommand, disconnectMonitorWs,
   clearCache: rcClearCache,
   resetNetwork: rcResetNetwork,
   factoryReset: rcFactoryReset,
 } = useMoControllerStore();
 const [cacheClearing, setCacheClearing] = useState(false);
 const [networkResetting, setNetworkResetting] = useState(false);
 const [factoryResetting, setFactoryResetting] = useState(false);
 // 전원 종료 확인 모달 — 이 동작만 window.confirm 을 쓰지 않는다. 되돌릴 수 없고,
 // 대부분의 경우 조작자가 실제로 원하는 것은 "화면 끄기"라서 그 대안을 함께 보여줘야 한다.
 const [shutdownConfirm, setShutdownConfirm] = useState(false);
 const [shutdownSent, setShutdownSent] = useState(false);
 const handleRcClearCache = async () => {
   if (!confirm('이 디바이스가 가지고 있는 모든 영상 파일을 삭제합니다. 계속하시겠어요?\n\n삭제 후엔 서버에서 다시 다운로드합니다 (네트워크 부하 + 시간 소요).')) {
     return;
   }
   setCacheClearing(true);
   try {
     const result = await rcClearCache();
     if (result.success) {
       alert(`✓ 캐시 비우기 완료${result.removed !== undefined ? ` (${result.removed}개 파일 삭제)` : ''}`);
     } else {
       alert(`✗ 캐시 비우기 실패: ${result.error}`);
     }
   } finally {
     setCacheClearing(false);
   }
 };
 const handleRcResetNetwork = async () => {
   if (!confirm('⚠ Wi-Fi 초기화\n\n디바이스의 저장된 모든 Wi-Fi 자격증명이 삭제됩니다.\n라우터의 WPS 버튼을 눌러 새로 연결해야 합니다 (디바이스가 자동 재시도).\n\n잘못된 SSID 에 연결됐을 때만 사용. 진행할까요?')) {
     return;
   }
   setNetworkResetting(true);
   try {
     const result = await rcResetNetwork();
     if (result.success) {
       alert(`✓ Wi-Fi 초기화 완료${result.removed_credentials !== undefined ? ` (${result.removed_credentials}개 자격증명 삭제)` : ''}\n\n이제 라우터의 WPS 버튼을 눌러 새로 연결해 주세요.\n디바이스 화면에서 "WPS WAIT" 진행 상황 확인 가능.`);
     } else {
       alert(`✗ Wi-Fi 초기화 실패: ${result.error}`);
     }
   } finally {
     setNetworkResetting(false);
   }
 };
 const handleRcFactoryReset = async () => {
   // DB 의 name 에 trailing space 가 있을 수 있어 비교 양쪽 모두 trim.
   const deviceName = (selectedClient?.name || '').trim();
   if (!deviceName) {
     alert('디바이스 이름을 확인할 수 없어 공장 초기화를 진행할 수 없습니다.');
     return;
   }
   if (!confirm(
     `⚠ 공장 초기화 — 디바이스 재배포 ⚠\n\n` +
     `이 디바이스를 공장 상태로 되돌립니다. 다음이 모두 삭제됩니다:\n` +
     `  • 등록 정보 (이전 매장과의 연결 해제)\n` +
     `  • 다운로드된 모든 영상\n` +
     `  • 인증키 / P2P 티켓 / 식별자\n\n` +
     `디바이스에 새 QR 코드가 표시되어 다른 매장에서 새로 등록할 수 있게 됩니다.\n` +
     `취소할 수 없습니다. 진행할까요?`
   )) {
     return;
   }
   const typed = prompt(
     `취소할 수 없는 destructive 작업입니다.\n` +
     `확인을 위해 디바이스 이름을 정확히 입력해 주세요:\n\n  "${deviceName}"`
   );
   if (typed === null) return;
   if (typed.trim() !== deviceName) {
     alert('디바이스 이름이 일치하지 않아 취소했습니다.');
     return;
   }
   setFactoryResetting(true);
   try {
     const result = await rcFactoryReset();
     if (result.success) {
       const parts: string[] = [];
       if (result.moClientsAffected != null) parts.push(`mo_clients ${result.moClientsAffected}건`);
       if (result.moClientFamiliesAffected != null) parts.push(`관계 ${result.moClientFamiliesAffected}건`);
       if (result.forwarded === false) parts.push('디바이스 오프라인 — 다음 부팅 시 자동 정리');
       alert(`✓ 공장 초기화 완료${parts.length ? `\n${parts.join(', ')}` : ''}`);
     } else {
       alert(`✗ 공장 초기화 실패: ${result.error}`);
     }
   } finally {
     setFactoryResetting(false);
   }
 };

 const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
   const level = Number(e.target.value);
   setLocalVolume(level);
   if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
   volumeTimerRef.current = setTimeout(() => { rcSendCommand('SetVolume', { level }); }, 300);
 }, [rcSendCommand]);

 const rcState = playerStatus?.state || '';
 const rcIsPlaying = rcState === 'playing';
 const rcIsPaused = rcState === 'paused';
 const rcIsStopped = rcState === 'stopped' || rcState === 'idle' || !rcState;
 const rcDisplayVolume = localVolume ?? playerStatus?.volume ?? 50;

 // Map file paths (e.g. "./files/File-xxx.mp4") to friendly names via cached_names
 const fileNameMap: Record<string, string> = detailedStatus?.downloads?.cached_names || {};
 const resolveFileName = (filePath: string): string => {
   // Extract "File-xxx" from paths like "./files/File-xxx.mp4" or "File-xxx_0000.ts"
   const match = filePath.match(/(File-[a-f0-9]+)/);
   if (match) {
     const name = fileNameMap[match[1]];
     if (name) return name;
   }
   return filePath;
 };

 // Sync local volume with player status
 useEffect(() => {
   if (playerStatus && localVolume === null) {
     setLocalVolume(playerStatus.volume);
   }
 }, [playerStatus, localVolume]);

 // Cleanup on unmount
 useEffect(() => {
   return () => {
     disconnectMonitorWs();
     if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
   };
 }, []);

 // Update component versions from WS push (only when changed)
 useEffect(() => {
   if (wsComponentVersions && selectedClient) {
     const cur = selectedClient.componentVersions;
     // 이름별 비교는 새 컴포넌트를 놓친다 — 통째로 비교한다.
     if (JSON.stringify(cur || {}) !== JSON.stringify(wsComponentVersions)) {
       setSelectedClient(prev => prev ? { ...prev, componentVersions: wsComponentVersions } : prev);
     }
   }
 }, [wsComponentVersions]);

 // Load user's owned families and registered clients
 useEffect(() => {
 if (user?.moPeerHash) {
 loadUserFamilies();
 loadRegisteredClients();
 }
 }, [user?.moPeerHash]);
 
 const loadUserFamilies = async () => {
 try {
 const response = await fetch('/mofamily/my-families', {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 setUserOwnedFamilies(data.data || []);
 }
 } catch (error) {
 console.error('Error loading user families:', error);
 }
 };
 
 const loadRegisteredClients = async () => {
 setLoadingClients(true);
 try {
 const response = await fetch('/moclient-test/list', {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 setRegisteredClients(data.data || []);
 } else {
 const errorData = await response.json();
 console.error('Error response:', response.status, errorData);
 if (response.status === 401) {
 alert('로그인이 필요합니다.');
 } else if (response.status === 403) {
 console.error('권한 없음:', errorData);
 } else {
 alert(errorData.error || 'MoClient 목록을 불러오는데 실패했습니다.');
 }
 }
 } catch (error) {
 console.error('Error loading registered clients:', error);
 } finally {
 setLoadingClients(false);
 }
 };
 
 const generateMoClientHash = async () => {
 try {
 // Generate random MAC address for testing
 const macAddress = Array(6).fill(0).map(() => 
 Math.floor(Math.random() * 256).toString(16).padStart(2, '0')
 ).join(':');
 
 const response = await fetch('/moclient-test/generate-hash', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ macAddress })
 });
 
 const data = await response.json();
 if (data.success) {
 setGeneratedMoClientHash(data.data.moClientHash);
 setMoClientHash(data.data.moClientHash);
 } else {
 alert(data.error || '해시 생성에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error generating hash:', error);
 alert('해시 생성 중 오류가 발생했습니다.');
 }
 };
 
 const refreshMonitors = async (clientHash: string) => {
   try {
     const monRes = await fetch(`/moclient-test/monitors/${clientHash}`, { credentials: 'include' });
     if (monRes.ok) {
       const monData = await monRes.json();
       setMonitors(Array.isArray(monData.monitors) ? monData.monitors : []);
     } else {
       setMonitors([]);
     }
   } catch {
     setMonitors([]);
   }
 };

 const toggleAutoFix = async (clientHash: string, connector: string, activate: boolean) => {
   try {
     const url = `/moclient-test/monitors/${clientHash}/${encodeURIComponent(connector)}/auto-fix`;
     const res = await fetch(url, {
       method: activate ? 'POST' : 'DELETE',
       credentials: 'include',
     });
     if (!res.ok) {
       const err = await res.json().catch(() => ({}));
       alert(err.error || `자동 보정 ${activate ? '활성화' : '비활성화'} 실패`);
       return;
     }
     await refreshMonitors(clientHash);
     if (activate) {
       alert('자동 보정 활성화됨. 디바이스 재부팅 후 적용됩니다.');
     }
   } catch (e) {
     alert(`요청 중 오류: ${e}`);
   }
 };

 const loadClientDetail = async (clientHash: string) => {
 setLoadingDetail(true);
if (showRemote) { disconnectMonitorWs(); setShowRemote(false); setLocalVolume(null); }
 try {
 const response = await fetch(`/moclient-test/detail/${clientHash}`, {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 setSelectedClient(data.data);
 setEditForm({
 address: data.data.address || '',
 comment: data.data.comment || '',
 latitude: data.data.latitude ?? null,
 longitude: data.data.longitude ?? null
 });
 setShowDetailModal(true);
 // Advanced 섹션의 모니터 정보 fetch — detail 과 같은 시점에. 실패해도 모달은 열림.
 await refreshMonitors(clientHash);
 } else {
 const errorData = await response.json();
 alert(errorData.error || 'MoClient 상세 정보를 불러오는데 실패했습니다.');
 }
 } catch (error) {
 console.error('Error loading client detail:', error);
 alert('MoClient 상세 정보를 불러오는 중 오류가 발생했습니다.');
 } finally {
 setLoadingDetail(false);
 }
 };
 
 const refreshClientStatus = async (clientHash: string) => {
   setRefreshingStatus(true);
   try {
     const response = await fetch(`/moclient-test/detail/${clientHash}`, {
       credentials: 'include'
     });
     if (response.ok) {
       const data = await response.json();
       setSelectedClient(prev => prev ? {
         ...prev,
         lastHeartbeat: data.data.lastHeartbeat,
         nowPlaying: data.data.nowPlaying,
         nowPlayingFilename: data.data.nowPlayingFilename,
         nowPlayingThumbnail: data.data.nowPlayingThumbnail,
         componentVersions: data.data.componentVersions,
         platform: data.data.platform,
       } : prev);
     }
   } catch (error) {
     console.error('Error refreshing client status:', error);
   } finally {
     setRefreshingStatus(false);
   }
 };

 const uploadPhotos = async (files: FileList | null) => {
   if (!selectedClient || !files || files.length === 0) return;
   const current = selectedClient.installationPhotos?.length ?? 0;
   if (current + files.length > 10) {
     alert(`사진은 최대 10장까지 업로드 가능합니다 (현재 ${current}장).`);
     return;
   }
   setPhotoUploading(true);
   try {
     const fd = new FormData();
     Array.from(files).forEach(f => fd.append('photos', f));
     const response = await fetch(`/moclient-test/${selectedClient.clientHash}/photos`, {
       method: 'POST',
       credentials: 'include',
       body: fd,
     });
     const data = await response.json();
     if (data.success) {
       setSelectedClient(prev => prev ? { ...prev, installationPhotos: data.data } : prev);
     } else {
       alert(data.error || '업로드 실패');
     }
   } catch (e) {
     console.error('Photo upload error:', e);
     alert('업로드 실패');
   } finally {
     setPhotoUploading(false);
     if (photoInputRef.current) photoInputRef.current.value = '';
   }
 };

 const deletePhoto = async (filename: string) => {
   if (!selectedClient) return;
   if (!confirm('이 사진을 삭제하시겠습니까?')) return;
   try {
     const response = await fetch(
       `/moclient-test/${selectedClient.clientHash}/photos/${filename}`,
       { method: 'DELETE', credentials: 'include' }
     );
     const data = await response.json();
     if (data.success) {
       setSelectedClient(prev => prev ? { ...prev, installationPhotos: data.data } : prev);
     } else {
       alert(data.error || '삭제 실패');
     }
   } catch (e) {
     console.error('Photo delete error:', e);
     alert('삭제 실패');
   }
 };

 const deleteClient = async () => {
 if (!selectedClient) return;
 
 if (!confirm(`정말로 "${selectedClient.name}" 스크린을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
 return;
 }
 
 try {
 const response = await fetch(`/moclient-test/delete/${selectedClient.clientHash}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 
 const data = await response.json();
 if (data.success) {
 alert('스크린이 삭제되었습니다.');
 setShowDetailModal(false);
 setSelectedClient(null);
 // 목록 새로고침
 await loadRegisteredClients();
 } else {
 alert(data.error || '스크린 삭제에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error deleting client:', error);
 alert('스크린 삭제 중 오류가 발생했습니다.');
 }
 };
 
 const updateClientInfo = async () => {
 if (!selectedClient) return;
 
 try {
 const response = await fetch(`/moclient-test/update/${selectedClient.clientHash}`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(editForm)
 });
 
 const data = await response.json();
 if (data.success) {
 alert(data.data.message);
 setEditMode(false);
 // 상세 정보 다시 로드
 await loadClientDetail(selectedClient.clientHash);
 // 목록도 새로고침
 await loadRegisteredClients();
 } else {
 alert(data.error || 'MoClient 정보 업데이트에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error updating client:', error);
 alert('MoClient 정보 업데이트 중 오류가 발생했습니다.');
 }
 };
 
 const registerMoClient = async () => {
 if (!moClientHash) {
 alert('MoClient 해시를 입력해주세요.');
 return;
 }
 
 if (!selectedMoFamilyForClient) {
 alert('모패밀리를 선택해주세요.');
 return;
 }
 
 setIsRegisteringClient(true);
 try {
 const response = await fetch('/moclient-test/register', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({
 moClientHash,
 moFamilyHash: selectedMoFamilyForClient
 })
 });
 
 const data = await response.json();
 if (data.success) {
 alert(data.data.message);
 setShowMoClientModal(false);
 setMoClientHash('');
 setSelectedMoFamilyForClient('');
 setGeneratedMoClientHash('');
 setScannedDeviceName('');
 // 등록된 클라이언트 목록 새로고침
 await loadRegisteredClients();
 } else {
 alert(data.error || 'MoClient 등록에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error registering MoClient:', error);
 alert('MoClient 등록 중 오류가 발생했습니다.');
 } finally {
 setIsRegisteringClient(false);
 }
 };
 
 const loadTicketPool = async (client: MoClient) => {
 setLoadingTickets(true);
 setTicketPoolClient(client);
 try {
 const response = await fetch(`/moclient-test/hierarchy/${client.clientHash}/tickets`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setHierarchyTickets(data.data.tickets || []);
 setShowTicketPoolModal(true);
 } else {
 const errorData = await response.json();
 alert(errorData.error || '티켓풀 조회에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error loading ticket pool:', error);
 alert('티켓풀 조회 중 오류가 발생했습니다.');
 } finally {
 setLoadingTickets(false);
 }
 };
 
 return (
 <>
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">스크린 등록</h1>
 <p className="text-sm text-gray-500 leading-relaxed mb-6">
 MoClient 가 설치된 디바이스를 모니터에 연결하면 등록용 QR 코드가 화면에 표시됩니다.
 등록된 스크린은 웹 리모컨으로 어디서든 제어할 수 있습니다.
 </p>
 
 <div className="mb-6">
 <p className="text-gray-600 mb-4">MoClient를 모패밀리에 등록하여 영상을 재생할 수 있도록 합니다.</p>
 <div className="flex gap-3">
 <button
 onClick={() => setShowQrScanner(true)}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 flex items-center gap-2"
 >
 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
 </svg>
 QR Scan
 </button>
 <button
 onClick={() => setShowMoClientModal(true)}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 MoClient 등록 테스트
 </button>
 <button
 onClick={() => setShowWifiUsbModal(true)}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 title="WPS 안 되는 환경에서 USB로 Wi-Fi 설정 주입"
 >
 Wi-Fi USB 생성
 </button>
 </div>
 </div>
 
 {/* 등록된 스크린 목록 */}
 <div className="mt-8">
 <h2 className="text-xl font-semibold mb-4">등록된 스크린 목록</h2>
 {loadingClients ? (
 <div className="text-center py-8 text-gray-500">로딩 중...</div>
 ) : registeredClients.length === 0 ? (
 <div className="text-center py-8 text-gray-500">
 등록된 스크린이 없습니다.
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-4">
 {registeredClients.map((client) => (
 <div key={client.clientHash} className="border border-gray-200 p-4 hover:shadow-md transition-shadow">
 <div className="flex justify-between items-start mb-2">
 <h3 className="font-medium text-lg">{client.name}</h3>
 <div className="flex items-center gap-1">
 {client.rotation ? (
   <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800">
     세로 {client.rotation === 90 ? '↻90°' : '↺90°'}
   </span>
 ) : null}
 <span className={`px-2 py-1 text-xs ${
 client.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
 }`}>
 {client.isActive ? '활성' : '비활성'}
 </span>
 </div>
 </div>
 <div className="space-y-1 text-sm text-gray-600">
 <p><span className="font-medium">모패밀리:</span> {client.moFamilyName}</p>
 <p><span className="font-medium">해시:</span> <span className="font-mono text-xs">{client.clientHash.startsWith('MoClient-') ? `MoClient-${client.clientHash.substring(9, 15)}` : client.clientHash.substring(0, 6)}</span></p>
 <p><span className="font-medium">등록자:</span> {client.ownerName || client.ownerEmail}</p>
 <p><span className="font-medium">등록일:</span> {formatDate(client.createdAt)}</p>
 {client.lastHeartbeat && (
 <p><span className="font-medium">마지막 연결:</span> {formatTimeAgo(new Date(client.lastHeartbeat))}</p>
 )}
 {client.componentVersions && Object.keys(client.componentVersions).length > 0 && (
 <div className="mt-1">
 <span className="font-medium">버전:</span>
 <div className="ml-2 text-xs font-mono">
 {/* 이름을 박지 않는다 — 컴포넌트가 늘어도 화면이 따라온다(MoClientMonitor 주석 참조). */}
 {Object.entries(client.componentVersions).map(([name, ver]) => (
  <span key={name} className="mr-2">{name.replace('mpp_renderer', 'rndr').replace('momanager', 'mgr').replace('moclient', 'cli').replace('moplayer', 'ply')}:{ver}</span>
 ))}
 </div>
 </div>
 )}
 </div>
 <div className="mt-3 space-y-2">
 <button
 className="w-full px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white"
 onClick={() => loadClientDetail(client.clientHash)}
 >
 상세보기
 </button>
 <button
 className="w-full px-3 py-1 text-sm bg-gray-300 hover:bg-gray-400 text-gray-800"
 onClick={() => loadTicketPool(client)}
 >
 티켓풀 보기
 </button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 
 {/* MoClient Registration Modal */}
 {showMoClientModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
 <div className="bg-white p-6 max-w-2xl w-full">
 <div className="flex justify-between items-center mb-4">
 <h2 className="text-xl font-bold">MoClient 등록 테스트</h2>
 <button
 onClick={() => {
 setShowMoClientModal(false);
 setMoClientHash('');
 setGeneratedMoClientHash('');
 setSelectedMoFamilyForClient('');
 setScannedDeviceName('');
 }}
 className="text-gray-500 hover:text-gray-700"
 >
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 
 <div className="space-y-4">
 <div>
 <h3 className="font-semibold mb-2">1. MoClient 해시 생성 (테스트용)</h3>
 <button
 onClick={generateMoClientHash}
 className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm"
 >
 해시 생성
 </button>
 {generatedMoClientHash && (
 <div className="mt-2 p-3 bg-gray-100">
 <p className="text-sm text-gray-600">생성된 해시:</p>
 <p className="font-mono text-xs break-all">{generatedMoClientHash}</p>
 </div>
 )}
 </div>
 
 <div>
 <h3 className="font-semibold mb-2">2. MoClient 해시 입력 (QR 스캔 대체)</h3>
 <input
 type="text"
 value={moClientHash}
 onChange={(e) => setMoClientHash(e.target.value)}
 placeholder="MoClient-XXXXX..."
 className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 {scannedDeviceName && (
 <p className="mt-1 text-sm text-green-600">
 QR Scanned Device: {scannedDeviceName}
 </p>
 )}
 </div>
 
 <div>
 <h3 className="font-semibold mb-2">3. 모패밀리 선택</h3>
 <select
 value={selectedMoFamilyForClient}
 onChange={(e) => setSelectedMoFamilyForClient(e.target.value)}
 className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 >
 <option value="">모패밀리를 선택하세요</option>
 {userOwnedFamilies.map((family) => (
 <option key={family.moFamilyHash} value={family.moFamilyHash}>
 {family.name} ({family.moFamilyHash.substring(0, 15)}...)
 </option>
 ))}
 </select>
 </div>
 
 <div className="flex justify-end gap-2 pt-4">
 <button
 onClick={() => setShowMoClientModal(false)}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 취소
 </button>
 <button
 onClick={registerMoClient}
 disabled={isRegisteringClient || !moClientHash || !selectedMoFamilyForClient}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {isRegisteringClient ? '등록 중...' : 'MoClient 등록'}
 </button>
 </div>
 
 <div className="mt-4 p-4 bg-blue-50">
 <p className="text-sm text-blue-700">
 <strong>API Endpoints:</strong><br/>
 • 해시 생성: <code className="bg-blue-100 px-2 py-1">/moclient-test/generate-hash</code><br/>
 • MoClient 등록: <code className="bg-blue-100 px-2 py-1">/moclient-test/register</code>
 </p>
 </div>
 </div>
 </div>
 </div>
 )}
 
 {/* MoClient Detail Modal */}
 {showDetailModal && selectedClient && (
 <div className="fixed inset-0 bg-black bg-opacity-50 sm:flex sm:items-center sm:justify-center sm:p-4" style={{ zIndex: 9999 }}>
 <div className="bg-white p-4 w-full h-full overflow-y-auto sm:p-6 sm:max-w-3xl sm:h-auto sm:max-h-[90vh] sm:rounded">
 <div className="flex justify-between items-center mb-4">
 <h2 className="text-xl font-bold flex items-center gap-2">
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
 </svg>
 MoClient 상세 정보
 </h2>
 <button
 onClick={() => {
 setShowDetailModal(false);
 setSelectedClient(null);
 setEditMode(false);
 if (showRemote) { disconnectMonitorWs(); setShowRemote(false); setLocalVolume(null); }
 }}
 className="text-gray-500 hover:text-gray-700"
 >
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 
 {loadingDetail ? (
 <div className="text-center py-8 text-gray-500">로딩 중...</div>
 ) : (
 <div className="space-y-6">
 {/* 기본 정보 섹션 */}
 <div className="border-b pb-4">
 <h3 className="font-semibold text-lg mb-3">기본 정보</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="text-sm text-gray-600">이름</label>
 <p className="font-medium">{selectedClient.name}</p>
 </div>
 <div>
 <label className="text-sm text-gray-600">상태</label>
 <p>
 <span className={`px-2 py-1 text-xs ${
 selectedClient.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
 }`}>
 {selectedClient.isActive ? '활성' : '비활성'}
 </span>
 {selectedClient.rotation ? (
   <span className="ml-1 px-2 py-1 text-xs bg-purple-100 text-purple-800">
     세로 {selectedClient.rotation === 90 ? '↻90°' : '↺90°'}
   </span>
 ) : null}
 </p>
 </div>
 <div className="col-span-2">
 <label className="text-sm text-gray-600">해시</label>
 <div className="flex items-center gap-2 bg-gray-100 p-2">
   <p className="font-mono text-sm">
     {selectedClient.clientHash.startsWith('MoClient-')
       ? `MoClient-${selectedClient.clientHash.substring(9, 15)}`
       : selectedClient.clientHash.substring(0, 6)}
   </p>
   <button
     onClick={() => {
       navigator.clipboard.writeText(selectedClient.clientHash);
       const btn = document.activeElement as HTMLButtonElement;
       const orig = btn.textContent;
       btn.textContent = '복사됨!';
       setTimeout(() => { btn.textContent = orig; }, 1500);
     }}
     className="px-2 py-1 text-xs bg-white hover:bg-gray-200 border border-gray-300 text-gray-600 whitespace-nowrap"
   >
     복사
   </button>
 </div>
 </div>
 <div>
 <label className="text-sm text-gray-600">모패밀리</label>
 <p className="font-medium">{selectedClient.moFamilyName}</p>
 </div>
 <div>
 <label className="text-sm text-gray-600">등록자</label>
 <p className="font-medium">{selectedClient.ownerName || selectedClient.ownerEmail}</p>
 </div>
 </div>
 </div>
 
 {/* 상태 정보 섹션 */}
 <div className="border-b pb-4">
 <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-semibold text-lg">상태 정보</h3>
                  <button
                    onClick={() => refreshClientStatus(selectedClient.clientHash)}
                    disabled={refreshingStatus}
                    className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-50"
                    title="새로고침"
                  >
                    <svg className={`w-4 h-4 ${refreshingStatus ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="text-sm text-gray-600">마지막 활동 (Heartbeat)</label>
 <p className="font-medium" title={formatDateTime(selectedClient.lastHeartbeat)}>
 {selectedClient.lastHeartbeat
 ? formatTimeAgo(new Date(selectedClient.lastHeartbeat))
 : '연결 기록 없음'}
 </p>
 </div>
 <div>
 <label className="text-sm text-gray-600">현재 재생 중</label>
 {selectedClient.nowPlaying ? (
 <div className="flex items-center gap-2">
 {selectedClient.nowPlayingThumbnail && (
 <img
 src={selectedClient.nowPlayingThumbnail}
 alt=""
 className="w-16 h-9 object-cover"
 />
 )}
 <p className="text-sm">{selectedClient.nowPlayingFilename || '제목 없음'}</p>
 </div>
 ) : (
 <p className="font-medium text-gray-500">없음</p>
 )}
 </div>
 </div>
 </div>

 {/* 일일 상태 리포트 시각 (화면별) */}
 <ReportTimeEditor clientHash={selectedClient.clientHash} />

 {/* 화면 설치 방향(회전) — 세로 설치 지원 */}
 <RotationEditor
   clientHash={selectedClient.clientHash}
   initialRotation={selectedClient.rotation ?? 0}
   thumbnailUrl={selectedClient.nowPlayingThumbnail}
   progress={selectedClient.rotationVariantProgress}
   onChanged={() => loadClientDetail(selectedClient.clientHash)}
 />

 {/* 설치 사진 섹션 */}
 <div className="border-b pb-4">
   <div className="flex items-center justify-between mb-3">
     <h3 className="font-semibold text-lg">
       설치 사진
       <span className="ml-2 text-sm text-gray-500 font-normal">
         {selectedClient.installationPhotos?.length ?? 0}/10
       </span>
     </h3>
     <div>
       <input
         ref={photoInputRef}
         type="file"
         accept="image/*"
         multiple
         className="hidden"
         onChange={e => uploadPhotos(e.target.files)}
       />
       <button
         type="button"
         onClick={() => photoInputRef.current?.click()}
         disabled={photoUploading || (selectedClient.installationPhotos?.length ?? 0) >= 10}
         className="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
       >
         {photoUploading ? '업로드 중...' : '사진 추가'}
       </button>
     </div>
   </div>
   {(selectedClient.installationPhotos?.length ?? 0) === 0 ? (
     <p className="text-sm text-gray-500">등록된 사진이 없습니다.</p>
   ) : (
     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
       {selectedClient.installationPhotos!.map(photo => (
         <div key={photo.filename} className="relative group">
           <img
             src={photo.url}
             alt=""
             className="w-full aspect-square object-cover border border-gray-200 cursor-pointer"
             onClick={() => setPhotoLightbox(photo.url)}
           />
           <button
             type="button"
             onClick={() => deletePhoto(photo.filename)}
             className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
             aria-label="사진 삭제"
           >
             ×
           </button>
         </div>
       ))}
     </div>
   )}
 </div>

 {/* 위치 정보 섹션 */}
 <div className="border-b pb-4">
 <h3 className="font-semibold text-lg mb-3">위치 정보</h3>
 {editMode ? (
 <LocationMapEdit
 latitude={editForm.latitude}
 longitude={editForm.longitude}
 address={editForm.address}
 onChange={(loc) => setEditForm({ ...editForm, ...loc })}
 />
 ) : (
 <LocationMapView
 latitude={selectedClient.latitude ?? null}
 longitude={selectedClient.longitude ?? null}
 address={selectedClient.address ?? null}
 />
 )}
 </div>
 
 {/* 메모 섹션 */}
 <div className="border-b pb-4">
 <h3 className="font-semibold text-lg mb-3">메모</h3>
 {editMode ? (
 <textarea
 value={editForm.comment}
 onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
 maxLength={200}
 rows={3}
 placeholder="메모를 입력하세요 (최대 200자)"
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 ) : (
 <p className="font-medium whitespace-pre-wrap">{selectedClient.comment || '없음'}</p>
 )}
 {editMode && (
 <p className="text-xs text-gray-500 mt-1">{editForm.comment.length}/200</p>
 )}
 </div>
 
 {/* 시간 정보 섹션 */}
 <div>
 <h3 className="font-semibold text-lg mb-3">시간 정보</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className="text-sm text-gray-600">등록일</label>
 <p className="font-medium">{formatDateTime(selectedClient.createdAt)}</p>
 </div>
 <div>
 <label className="text-sm text-gray-600">마지막 수정일</label>
 <p className="font-medium">{formatDateTime(selectedClient.updatedAt)}</p>
 </div>
 </div>
 </div>

 {/* 플랫폼만 표시. 컴포넌트 버전은 노드 모니터에서 정확히 보이므로 중복 제거. */}
 {selectedClient.platform && (
 <div>
 <label className="text-sm text-gray-600">플랫폼</label>
 <p className="font-mono text-sm font-medium">{selectedClient.platform}</p>
 </div>
 )}

 {/* 노드 모니터 (TUI-style) */}
 {showRemote && (
 <div className="border-b pb-4">
   <h3 className="font-semibold text-lg mb-3">노드 모니터</h3>
   <MoClientMonitor
     detailedStatus={detailedStatus}
     managerStatus={managerStatus}
     connectionState={connectionState}
     clientHash={selectedClient.clientHash}
   />
 </div>
 )}

 {/* 리모컨 섹션 */}
 {showRemote && (
 <div className="border-t pt-4">
 <div className="flex items-center gap-3 mb-3">
   <h3 className="font-semibold text-lg">리모컨</h3>
   <div className="flex items-center gap-3 text-sm">
     <div className="flex items-center gap-1.5">
       <span className={`w-2 h-2 ${connectionState === 'connected' ? 'bg-green-500' : connectionState === 'connecting' ? 'bg-yellow-400 animate-pulse' : 'bg-gray-400'}`} />
       <span className="text-gray-500">{connectionState === 'connected' ? '실시간' : connectionState === 'connecting' ? '연결 중...' : '폴링'}</span>
     </div>
     <div className="flex items-center gap-1.5">
       <span className={`w-2 h-2 ${playerConnected ? 'bg-green-500' : 'bg-red-400'}`} />
       <span className="text-gray-500">MoPlayer {playerConnected ? '연결됨' : '끊김'}</span>
     </div>
   </div>
 </div>

 {!playerConnected ? (
   <p className="text-sm text-gray-400 py-4 text-center">MoPlayer가 연결되지 않았습니다</p>
 ) : (
   <div className="space-y-3">
     {/* 플레이어 상태 */}
     {playerStatus && (
       <div className="flex items-center gap-3 text-sm">
         <span className={`px-2 py-0.5 text-xs font-medium ${
           rcIsPlaying ? 'bg-green-100 text-green-800' :
           rcIsPaused ? 'bg-yellow-100 text-yellow-800' :
           rcState === 'stopped' ? 'bg-red-100 text-red-800' :
           'bg-gray-100 text-gray-600'
         }`}>
           {rcIsPlaying ? '재생 중' : rcIsPaused ? '일시정지' : rcState === 'stopped' ? '정지' : '대기'}
         </span>
         {/* 화면 상태 — 렌더러가 커널 CRTC ACTIVE 를 읽어 보고한 **관측값**이다.
             "껐다"는 명령이 전달된 것과 실제로 꺼진 것은 다르고, 원격 운영자가 알아야 하는
             건 후자다. 원격지 화면은 눈으로 확인할 수 없으므로 이 표시가 유일한 근거다. */}
         {playerStatus.screen_off && (
           <span className="px-2 py-0.5 text-xs font-medium bg-gray-800 text-white" title="렌더러가 보고한 실제 상태 — HDMI 신호가 꺼져 TV 가 대기모드입니다.">
             화면 꺼짐
           </span>
         )}
         {playerStatus.current_video && (
           <span className="text-gray-600 truncate">{resolveFileName(playerStatus.current_video)}</span>
         )}
       </div>
     )}

     {/* 재생 컨트롤 */}
     <div className="flex items-center gap-2 justify-center">
       <button onClick={() => rcSendCommand('Previous')} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm" title="이전">&#9198;</button>
       {rcIsPlaying ? (
         <button onClick={() => rcSendCommand('Stop')} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm">Stop</button>
       ) : (
         <button onClick={() => rcSendCommand('Play')} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm">Play</button>
       )}
       {rcIsPaused ? (
         <button onClick={() => rcSendCommand('Resume')} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm">Resume</button>
       ) : (
         <button onClick={() => rcSendCommand('Pause')} disabled={!rcIsPlaying} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm disabled:opacity-40">Pause</button>
       )}
       <button onClick={() => rcSendCommand('Next')} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm" title="다음">&#9197;</button>
     </div>

     {/* 오버레이 토글 */}
     <div className="flex items-center gap-2 justify-center">
       <span className="text-sm text-gray-500">화면</span>
       <button onClick={() => rcSendCommand('ToggleStatus')} className={`px-3 py-1.5 text-xs border transition ${overlayToggles.show_status ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}>Status</button>
       <button onClick={() => rcSendCommand('ToggleDebug')} className={`px-3 py-1.5 text-xs border transition ${overlayToggles.show_debug ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}>Debug</button>
       <button onClick={() => rcSendCommand('ToggleInfo')} className={`px-3 py-1.5 text-xs border transition ${overlayToggles.show_info ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}>Info</button>
       <button onClick={() => rcSendCommand('ToggleQr')} className={`px-3 py-1.5 text-xs border transition ${overlayToggles.qr_visible ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}>QR</button>
       <button
         onClick={() => rcSendCommand('ToggleHwaccel')}
         title="HW 디코딩 변경은 렌더러(오버레이·화질·종횡비) 전환을 포함하므로, 장비(moplayer) 재시작 후에 완전히 적용됩니다. 지금 껐다 켜는 것은 디코더에만 즉시 반영됩니다."
         className={`px-3 py-1.5 text-xs border transition ${overlayToggles.hwaccel_active ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}
       >HW<sup className="ml-0.5 opacity-70">?</sup></button>
       <button onClick={() => rcSendCommand('TogglePlayLog')} className={`px-3 py-1.5 text-xs border transition ${overlayToggles.play_log_enabled ? 'bg-blue-500 text-white border-blue-600' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'}`}>{overlayToggles.play_log_enabled ? '로그 ON' : '로그'}</button>
     </div>

     {/* 디바이스 관리 — destructive 액션 한 줄: 캐시 비우기 | Wi-Fi 초기화 | 공장 초기화 | 재부팅 */}
     <div className="flex items-center gap-2 justify-center pt-2 border-t border-gray-200">
       <button
         onClick={handleRcClearCache}
         disabled={cacheClearing}
         className="px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white border border-red-600 transition"
         title="디바이스의 ./files 캐시 모두 삭제 후 재다운로드 트리거"
       >
         {cacheClearing ? '비우는 중...' : '캐시 비우기'}
       </button>
       <button
         onClick={handleRcResetNetwork}
         disabled={networkResetting}
         className="px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white border border-red-600 transition"
         title="저장된 모든 Wi-Fi 자격증명 삭제 + WPS PBC 무한 재시도. 잘못된 SSID 에 연결됐을 때 회복용."
       >
         {networkResetting ? '초기화 중...' : 'Wi-Fi 초기화'}
       </button>
       <button
         onClick={handleRcFactoryReset}
         disabled={factoryResetting}
         className="px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white border border-red-600 transition"
         title="디바이스를 다른 매장에 재배포할 때 사용. 등록·캐시·인증키·P2P 티켓 모두 초기화 후 새 QR 표시. 취소 불가."
       >
         {factoryResetting ? '초기화 중...' : '공장 초기화'}
       </button>
       {/* 화면 끄기/켜기 — 영업 종료 후 TV 를 대기모드로. 기기는 켜진 채라 여기서 다시 켠다
           (전원 종료와 달리 사람이 현장에 갈 필요가 없다). */}
       <button
         onClick={() => rcSendCommand('ScreenOff')}
         className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-800 text-white border border-gray-800 transition"
         title="HDMI 신호를 끊어 TV 를 대기모드로 보냅니다. 기기는 켜져 있어 언제든 다시 켤 수 있습니다."
       >
         화면 끄기
       </button>
       <button
         onClick={() => rcSendCommand('ScreenOn')}
         className="px-3 py-1.5 text-xs bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 transition"
         title="꺼 둔 화면을 다시 켭니다."
       >
         화면 켜기
       </button>
       <button
         onClick={() => {
           if (window.confirm('이 디바이스를 재부팅하시겠습니까?\n재부팅 중 약 1분간 재생이 중단됩니다.')) {
             rcSendCommand('Reboot');
           }
         }}
         className="px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 text-white border border-red-600 transition"
         title="디바이스 재부팅"
       >
         재부팅
       </button>
       {/* 전원 종료 — 최후의 수단. 누르면 바로 끄지 않고 확인 모달을 연다. */}
       <button
         onClick={() => { setShutdownSent(false); setShutdownConfirm(true); }}
         className="px-3 py-1.5 text-xs bg-red-700 hover:bg-red-800 text-white border border-red-900 transition"
         title="장비 전원을 완전히 끕니다. 원격으로 다시 켤 수 없습니다."
       >
         전원 종료
       </button>
     </div>

     {/* 전원 종료 확인 — ⚠️ 되돌릴 수 없는 유일한 원격 동작이다.
         기본 버튼은 취소이고, 조작자가 실제로 원했을 가능성이 높은 "화면 끄기"를
         같은 모달에서 바로 고를 수 있게 한다(그건 원격으로 되돌릴 수 있다). */}
     {shutdownConfirm && (
       <div
         className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
         onClick={() => setShutdownConfirm(false)}
       >
         <div
           className="bg-white max-w-md w-full p-5 border border-gray-300 shadow-lg"
           onClick={(e) => e.stopPropagation()}
         >
           <h3 className="text-base font-semibold text-red-700">장비 전원을 끕니다</h3>
           <p className="mt-3 text-sm text-gray-800">
             대상: <span className="font-mono font-semibold">{selectedClient?.name || '(이름 없음)'}</span>
           </p>
           <div className="mt-3 p-3 bg-red-50 border border-red-200 text-sm text-red-800">
             <p className="font-semibold">원격으로 다시 켤 수 없습니다.</p>
             <p className="mt-1">
               전원이 꺼지면 이 화면을 포함해 어떤 원격 수단으로도 되살릴 수 없습니다.
               누군가 현장에 가야 합니다.
             </p>
             {/* ⚠️ 실기 확인(2026-08-09, M1S): 소프트웨어 종료 뒤 PMIC 가 OFF 로 래치된다.
                 멀티탭 스위치를 껐다 켜는 것만으로는 안 켜졌다 — 어댑터 출력·보드 벌크
                 캐패시터에 전하가 남아 레일이 PMIC 리셋 문턱 아래로 안 내려가기 때문.
                 보드 쪽 잭을 뽑으면 즉시 방전돼 재삽입 시 부팅한다.
                 "전원 버튼을 누르세요"라고 적어 두면 현장에서 멀티탭만 만지고 고장으로
                 오판한다 — 되살리는 **실제 동작**을 적는다. */}
             <p className="mt-2">
               다시 켜려면 <span className="font-semibold">보드에 꽂힌 어댑터 잭을 뽑았다가 다시 꽂아야</span> 합니다.
               멀티탭 스위치만 껐다 켜면 켜지지 않을 수 있습니다.
             </p>
           </div>
           <div className="mt-3 p-3 bg-gray-50 border border-gray-200 text-sm text-gray-700">
             <p>영업 종료 후 화면만 끄려는 것이라면 <span className="font-semibold">화면 끄기</span>를 쓰세요 — 전력·발열이 내려가고, 원격에서 언제든 다시 켤 수 있습니다.</p>
           </div>
           <div className="mt-4 flex items-center justify-end gap-2">
             <button
               onClick={() => setShutdownConfirm(false)}
               className="px-3 py-1.5 text-sm bg-white hover:bg-gray-50 text-gray-800 border border-gray-300"
             >
               취소
             </button>
             <button
               onClick={() => { rcSendCommand('ScreenOff'); setShutdownConfirm(false); }}
               className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-800 text-white border border-gray-800"
             >
               화면만 끄기
             </button>
             <button
               onClick={() => { rcSendCommand('Shutdown'); setShutdownSent(true); setShutdownConfirm(false); }}
               className="px-3 py-1.5 text-sm bg-red-700 hover:bg-red-800 text-white border border-red-900"
             >
               전원 끄기
             </button>
           </div>
         </div>
       </div>
     )}

     {shutdownSent && (
       <div className="mt-2 p-3 bg-gray-100 border border-gray-300 text-sm text-gray-700">
         전원 종료 명령을 보냈습니다. 장비가 곧 응답을 멈춥니다 — 목록에서 오프라인으로 바뀌면 종료된 것입니다.
         <span className="block mt-1 text-gray-600">계속 온라인이면 종료가 실패한 것이니 알려 주세요.</span>
       </div>
     )}

     {/* 볼륨 — 사운드 처리 미구현 상태라 hidden. 코드 보존. */}
     <div className="hidden">
       <span className="text-sm text-gray-500">볼륨</span>
       <input type="range" min={0} max={100} value={rcDisplayVolume} onChange={handleVolumeChange} className="flex-1" />
       <span className="text-sm font-mono w-8 text-right">{rcDisplayVolume}</span>
     </div>

     {/* 재생 목록 */}
     {playerStatus?.playlist && playerStatus.playlist.length > 0 && (
       <div>
         <p className="text-sm text-gray-500 mb-1">재생 목록</p>
         <ul className="space-y-0.5 max-h-32 overflow-y-auto">
           {playerStatus.playlist.map((file, idx) => {
             const isCurrent = playerStatus.current_video === file;
             return (
               <li key={idx} onClick={() => rcSendCommand('PlayVideo', { path: file })}
                 className={`px-3 py-1.5 text-sm cursor-pointer ${isCurrent ? 'bg-blue-50 text-blue-800 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}>
                 {isCurrent && <span className="mr-1">&#9654;</span>}{resolveFileName(file)}
               </li>
             );
           })}
         </ul>
       </div>
     )}

     {/* 최근 명령 */}
     {recentCommands.length > 0 && (
       <div className="flex flex-wrap gap-1">
         {recentCommands.slice(0, 5).map((cmd, idx) => (
           <span key={`${cmd.timestamp}-${idx}`} className={`px-2 py-0.5 text-xs ${
             cmd.delivery === 'realtime' || cmd.delivery === 'channel' ? 'bg-green-100 text-green-700' :
             cmd.delivery === 'queued' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
           }`}>
             {cmd.command}
           </span>
         ))}
       </div>
     )}
   </div>
 )}
 </div>
 )}

 {/* Advanced — 모니터 진단 */}
 <div className="border-b pb-4">
   <button
     type="button"
     onClick={() => setShowAdvanced(!showAdvanced)}
     className="flex items-center gap-2 font-semibold text-lg mb-3 text-gray-700 hover:text-gray-900"
   >
     <svg className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
     </svg>
     Advanced — 모니터 진단
   </button>
   {showAdvanced && (
     <div>
       {monitors.length === 0 ? (
         <p className="text-sm text-gray-500">
           아직 보고된 모니터 정보가 없습니다. (MoClient 시작 시 1회 보고)
         </p>
       ) : (
         <div className="overflow-x-auto">
           <table className="w-full text-sm">
             <thead className="bg-gray-50">
               <tr className="text-left">
                 <th className="px-2 py-1 font-medium">Connector</th>
                 <th className="px-2 py-1 font-medium">Status</th>
                 <th className="px-2 py-1 font-medium">제조사 / 모델</th>
                 <th className="px-2 py-1 font-medium">이름</th>
                 <th className="px-2 py-1 font-medium">Preferred</th>
                 <th className="px-2 py-1 font-medium">Available modes</th>
                 <th className="px-2 py-1 font-medium">EDID 진단</th>
               </tr>
             </thead>
             <tbody>
               {monitors.map((m) => {
                 const issueCount = m.diagnostics?.issues.length ?? 0;
                 const isExpanded = expandedConn === m.connector;
                 const rowBg = issueCount > 0 ? 'bg-yellow-50' : '';
                 return (
                   <React.Fragment key={m.connector}>
                     <tr className={`border-t ${rowBg}`}>
                       <td className="px-2 py-1 font-mono">{m.connector}</td>
                       <td className="px-2 py-1">
                         <span className={`px-2 py-0.5 text-xs ${
                           m.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                         }`}>
                           {m.status}
                         </span>
                       </td>
                       <td className="px-2 py-1">
                         {m.pnp_id ? `${m.pnp_id}${m.product_code !== null ? ` / ${m.product_code}` : ''}` : '-'}
                       </td>
                       <td className="px-2 py-1">{m.monitor_name || '-'}</td>
                       <td className="px-2 py-1 font-mono">{m.preferred_mode || '-'}</td>
                       <td className="px-2 py-1 text-xs text-gray-700">
                         {(m.available_modes && m.available_modes.length > 0)
                           ? m.available_modes.join(', ')
                           : '-'}
                       </td>
                       <td className="px-2 py-1">
                         {m.diagnostics === null ? (
                           <span className="text-gray-400 text-xs">N/A</span>
                         ) : issueCount === 0 && !m.auto_fix_active ? (
                           <span className="text-green-700 text-xs">✓ OK</span>
                         ) : (
                           <button
                             type="button"
                             onClick={() => setExpandedConn(isExpanded ? null : m.connector)}
                             className={`text-xs hover:underline ${
                               issueCount > 0 ? 'text-yellow-700' : 'text-green-700'
                             }`}
                           >
                             {issueCount > 0 ? `⚠ ${issueCount}개 자기모순` : '✓ 보정 활성'}
                             {' '}{isExpanded ? '▲' : '▼'}
                           </button>
                         )}
                       </td>
                     </tr>
                     {isExpanded && m.diagnostics && issueCount > 0 && (
                       <tr className="bg-yellow-50">
                         <td colSpan={7} className="px-4 py-2 border-t border-yellow-200">
                           <ul className="space-y-1 text-xs">
                             {m.diagnostics.issues.map((iss, i) => (
                               <li key={i} className="flex gap-2">
                                 <span className="font-mono text-yellow-700">{iss.kind}</span>
                                 <span className="text-gray-700">{iss.msg}</span>
                               </li>
                             ))}
                           </ul>
                           <div className="mt-2 text-xs text-gray-500">
                             max DTD: {m.diagnostics.maxDtdHRateKhz} kHz / {m.diagnostics.maxDtdPclkMhz} MHz
                             {m.diagnostics.vsdbMaxTmdsMhz !== null && ` · VSDB TMDS: ${m.diagnostics.vsdbMaxTmdsMhz} MHz`}
                             {m.diagnostics.rangeLimits && ` · Range: H≤${m.diagnostics.rangeLimits.hMaxKhz} kHz, dotclock≤${m.diagnostics.rangeLimits.maxDotclockMhz} MHz`}
                           </div>
                           <div className="mt-3 flex items-center gap-3">
                             {m.auto_fix_active ? (
                               <>
                                 <span className="text-xs text-green-700">✓ 자동 보정 활성 — 재부팅 후 적용</span>
                                 <button
                                   type="button"
                                   onClick={() => toggleAutoFix(selectedClient!.clientHash, m.connector, false)}
                                   className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300"
                                 >
                                   비활성화
                                 </button>
                               </>
                             ) : (
                               <>
                                 <button
                                   type="button"
                                   onClick={() => toggleAutoFix(selectedClient!.clientHash, m.connector, true)}
                                   className="px-3 py-1 text-xs bg-yellow-600 text-white hover:bg-yellow-700"
                                 >
                                   자동 보정 활성화
                                 </button>
                                 <span className="text-xs text-gray-500">
                                   Range Limits 와 VSDB TMDS 만 풀어줌 (DTD 추가 X)
                                 </span>
                               </>
                             )}
                           </div>
                         </td>
                       </tr>
                     )}
                     {isExpanded && m.auto_fix_active && issueCount === 0 && (
                       <tr className="bg-green-50">
                         <td colSpan={7} className="px-4 py-2 border-t border-green-200 text-xs">
                           ✓ 자동 보정 적용됨 (재부팅 후 효력 발생)
                           <button
                             type="button"
                             onClick={() => toggleAutoFix(selectedClient!.clientHash, m.connector, false)}
                             className="ml-3 px-2 py-1 bg-gray-200 hover:bg-gray-300"
                           >
                             비활성화
                           </button>
                         </td>
                       </tr>
                     )}
                   </React.Fragment>
                 );
               })}
             </tbody>
           </table>
         </div>
       )}
     </div>
   )}
 </div>

 {/* 액션 버튼 */}
 <div className="flex justify-end gap-2 pt-4">
 {editMode ? (
 <>
 <button
 onClick={() => {
 setEditMode(false);
 setEditForm({
 address: selectedClient.address || '',
 comment: selectedClient.comment || '',
 latitude: selectedClient.latitude ?? null,
 longitude: selectedClient.longitude ?? null
 });
 }}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 취소
 </button>
 <button
 onClick={updateClientInfo}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 저장
 </button>
 </>
 ) : (
 <>
 <button
 onClick={() => {
   if (showRemote) {
     disconnectMonitorWs();
     setShowRemote(false);
     setLocalVolume(null);
   } else {
     rcSelectClient(selectedClient.clientHash);
     setShowRemote(true);
   }
 }}
 className={`px-4 py-2 ${showRemote ? 'bg-gray-400 hover:bg-gray-500 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
 >
 {showRemote ? '리모컨 닫기' : '리모컨'}
 </button>
 <button
 onClick={deleteClient}
 className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white"
 >
 삭제
 </button>
 <button
 onClick={() => setEditMode(true)}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 편집
 </button>
 <button
 onClick={() => { setShowDetailModal(false); setSelectedClient(null); setEditMode(false); if (showRemote) { disconnectMonitorWs(); setShowRemote(false); setLocalVolume(null); } }}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 닫기
 </button>
 </>
 )}
 </div>
 </div>
 )}
 </div>
 </div>
 )}
 
 {/* QR Scanner Modal */}
 <QrScannerModal
 isOpen={showQrScanner}
 onClose={() => setShowQrScanner(false)}
 onScanSuccess={(info) => {
 setShowQrScanner(false);
 setMoClientHash(info.moclient_hash);
 setScannedDeviceName(info.device_name);
 setShowMoClientModal(true);
 }}
 />

 {/* 설치 사진 lightbox */}
 {photoLightbox && (
   <div
     className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 cursor-zoom-out"
     style={{ zIndex: 10000 }}
     onClick={() => setPhotoLightbox(null)}
   >
     <img src={photoLightbox} alt="" className="max-w-full max-h-full object-contain" />
   </div>
 )}

 {/* Ticket Pool Modal */}
 {showTicketPoolModal && ticketPoolClient && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
 <div className="bg-white p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
 <div className="flex justify-between items-center mb-4">
 <h2 className="text-xl font-bold flex items-center gap-2">
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
 </svg>
 티켓풀 - {ticketPoolClient.name}
 </h2>
 <button
 onClick={() => {
 setShowTicketPoolModal(false);
 setHierarchyTickets([]);
 setTicketPoolClient(null);
 }}
 className="text-gray-500 hover:text-gray-700"
 >
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 
 {loadingTickets ? (
 <div className="text-center py-8 text-gray-500">티켓풀 로딩 중...</div>
 ) : hierarchyTickets.length === 0 ? (
 <div className="text-center py-8 text-gray-500">
 <p>접근 가능한 티켓이 없습니다.</p>
 <p className="text-sm mt-2">이 클라이언트는 기본 모패밀리가 없거나, 모패밀리에 P2P 티켓이 생성되지 않았습니다.</p>
 </div>
 ) : (
 <div className="space-y-4">
 <div className="bg-blue-50 p-4">
 <p className="text-sm text-blue-700 mb-2">
 <strong>계층 구조 티켓풀</strong>: 이 MoClient가 계층 구조를 통해 접근 가능한 모든 P2P 인증 티켓입니다.
 </p>
 <p className="text-xs text-blue-600">
 총 {hierarchyTickets.length}개의 티켓이 있으며, 같은 티켓을 가진 다른 MoClient와 P2P 통신이 가능합니다.
 </p>
 </div>
 
 {hierarchyTickets.map((ticket, index) => (
 <div key={index} className={`border p-4 ${
 ticket.hierarchyLevel === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
 }`}>
 <div className="flex items-start justify-between mb-2">
 <div className="flex items-center gap-2">
 {ticket.hierarchyLevel === 0 ? (
 <>
 <span className="text-blue-600">📍</span>
 <span className="text-sm font-semibold text-blue-700">기본 모패밀리</span>
 </>
 ) : (
 <>
 <span className="text-gray-500">↳</span>
 <span className="text-sm text-gray-600">계층 레벨 {ticket.hierarchyLevel}</span>
 </>
 )}
 </div>
 <span className="text-lg font-semibold">{ticket.familyName}</span>
 </div>
 
 <div className="space-y-1 text-sm">
 <div className="flex gap-2">
 <span className="text-gray-600 font-medium w-24">모패밀리 해시:</span>
 <span className="font-mono text-xs bg-gray-100 px-2 py-1 flex-1">
 {ticket.moFamilyHash}
 </span>
 </div>
 <div className="flex gap-2">
 <span className="text-gray-600 font-medium w-24">P2P 티켓:</span>
 <span className="font-mono text-xs bg-green-100 px-2 py-1 flex-1">
 {ticket.ticketHash}
 </span>
 </div>
 </div>
 </div>
 ))}
 
 <div className="mt-6 p-4 bg-gray-50">
 <p className="text-sm text-gray-700 font-medium mb-2">사용 방법:</p>
 <ul className="text-xs text-gray-600 space-y-1">
 <li>• 기본 모패밀리 (📍 표시)는 이 MoClient가 직접 속한 모패밀리입니다.</li>
 <li>• 상위 계층 티켓들은 모패밀리 간 관계를 통해 상속받은 티켓입니다.</li>
 <li>• 같은 P2P 티켓을 가진 MoClient들끼리 P2P 통신이 가능합니다.</li>
 </ul>
 </div>
 </div>
 )}
 
 <div className="flex justify-end mt-6">
 <button
 onClick={() => {
 setShowTicketPoolModal(false);
 setHierarchyTickets([]);
 setTicketPoolClient(null);
 }}
 className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
 닫기
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Wi-Fi USB 생성 모달 */}
 {showWifiUsbModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
 <div className="bg-white max-w-md w-full p-6">
 <h2 className="text-xl font-bold mb-2">Wi-Fi USB 생성</h2>
 <p className="text-sm text-gray-600 mb-4">
 WPS 가 안 되는 공유기 환경용. 다운로드된 <code className="font-mono text-xs bg-gray-100 px-1">moc_wifi.conf</code> 를
 USB 루트에 저장하고 장비에 꽂으면 자동 적용됩니다 (재부팅 불필요).
 </p>
 <div className="space-y-3">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">SSID (공유기 이름)</label>
 <input
   type="text"
   value={wifiUsbSsid}
   onChange={(e) => setWifiUsbSsid(e.target.value)}
   placeholder="예) MyHomeWiFi"
   className="w-full px-3 py-2 border border-gray-300"
   autoFocus
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">보안 방식</label>
 <select
   value={wifiUsbSecurity}
   onChange={(e) => setWifiUsbSecurity(e.target.value as 'WPA2' | 'WPA3' | 'OPEN')}
   className="w-full px-3 py-2 border border-gray-300 bg-white"
 >
   <option value="WPA2">WPA2 (대부분 공유기)</option>
   <option value="WPA3">WPA3</option>
   <option value="OPEN">Open (비번 없음)</option>
 </select>
 </div>
 {wifiUsbSecurity !== 'OPEN' && (
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 (8자 이상)</label>
 <input
   type="text"
   value={wifiUsbPsk}
   onChange={(e) => setWifiUsbPsk(e.target.value)}
   placeholder="공유기 비밀번호"
   className="w-full px-3 py-2 border border-gray-300 font-mono"
 />
 <p className="text-xs text-gray-500 mt-1">{wifiUsbPsk.length}자</p>
 </div>
 )}
 <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
 ⚠ USB 에 평문 비번이 들어갑니다. 설정 후 USB 는 즉시 빼서 보관하거나 포맷하세요.
 </div>
 </div>
 <div className="flex justify-end gap-2 mt-6">
 <button
   onClick={() => {
     setShowWifiUsbModal(false);
     setWifiUsbSsid('');
     setWifiUsbPsk('');
     setWifiUsbSecurity('WPA2');
   }}
   className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800"
 >
   취소
 </button>
 <button
   onClick={async () => {
     if (!wifiUsbSsid.trim()) {
       alert('SSID 를 입력하세요.');
       return;
     }
     if (wifiUsbSecurity !== 'OPEN' && wifiUsbPsk.length < 8) {
       alert('WPA2/WPA3 는 비번 8자 이상이어야 합니다.');
       return;
     }
     setWifiUsbDownloading(true);
     try {
       const resp = await fetch('/wifi-config/download', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         credentials: 'include',
         body: JSON.stringify({
           ssid: wifiUsbSsid.trim(),
           psk: wifiUsbPsk,
           security: wifiUsbSecurity,
         }),
       });
       if (!resp.ok) {
         const j = await resp.json().catch(() => ({}));
         alert(j.error || '다운로드에 실패했습니다.');
         return;
       }
       const blob = await resp.blob();
       const url = URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url;
       a.download = 'moc_wifi.conf';
       a.click();
       URL.revokeObjectURL(url);
       setShowWifiUsbModal(false);
       setWifiUsbSsid('');
       setWifiUsbPsk('');
       setWifiUsbSecurity('WPA2');
     } catch (e) {
       alert('네트워크 오류: ' + (e as Error).message);
     } finally {
       setWifiUsbDownloading(false);
     }
   }}
   disabled={wifiUsbDownloading}
   className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
 >
   {wifiUsbDownloading ? '생성 중...' : '다운로드'}
 </button>
 </div>
 </div>
 </div>
 )}
 </>
 );
};

export default MoClientPage;